import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

// Rounding rules for Scandinavian currencies (round to nearest 0.05)
const ROUND_TO_NICKEL_CURRENCIES = ['DKK', 'SEK', 'NOK'];

/**
 * Apply currency-specific rounding rules.
 * DKK, SEK, NOK: Standard mathematical rounding to nearest 0.05
 * Others: Standard 2 decimal rounding
 */
export const roundCurrencyAmount = (amount, currency) => {
    if (ROUND_TO_NICKEL_CURRENCIES.includes(currency)) {
        return Math.round(amount * 20) / 20;
    }
    return Math.round(amount * 100) / 100;
};

/**
 * Convert amount from EUR to target currency using rates.
 */
export const convertFromEur = (amount, toCurrency, rates, applyRounding = true) => {
    if (!rates || !toCurrency) return amount;
    if (toCurrency === 'EUR') return applyRounding ? roundCurrencyAmount(amount, 'EUR') : amount;
    
    const rate = rates[toCurrency];
    if (!rate) return amount;
    
    const converted = amount * rate;
    return applyRounding ? roundCurrencyAmount(converted, toCurrency) : converted;
};

/**
 * Convert amount between any two currencies using EUR-based rates.
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, rates, applyRounding = true) => {
    if (!rates || fromCurrency === toCurrency) {
        return applyRounding ? roundCurrencyAmount(amount, toCurrency) : amount;
    }
    
    // Convert to EUR first, then to target
    const fromRate = rates[fromCurrency] || 1;
    const toRate = rates[toCurrency] || 1;
    
    const amountInEur = amount / fromRate;
    const converted = amountInEur * toRate;
    
    return applyRounding ? roundCurrencyAmount(converted, toCurrency) : converted;
};

/**
 * Format currency amount with proper symbol and formatting.
 */
export const formatCurrencyAmount = (amount, currency, options = {}) => {
    const { showSymbol = true, showCode = false, decimals = 2 } = options;
    
    const symbols = {
        EUR: '€',
        DKK: 'kr',
        SEK: 'kr',
        NOK: 'kr',
        GBP: '£',
        USD: '$'
    };
    
    // Format number
    const formatted = amount.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
    
    if (showCode) {
        return `${formatted} ${currency}`;
    }
    
    if (showSymbol) {
        const symbol = symbols[currency] || currency;
        // For currencies with 'kr', put it after the amount
        if (['DKK', 'SEK', 'NOK'].includes(currency)) {
            return `${formatted} ${symbol}`;
        }
        return `${symbol}${formatted}`;
    }
    
    return formatted;
};


/**
 * Format a purchase / cost price. Displays UP TO 4 decimals — trailing
 * zeros are trimmed so 0.5 shows as "0.5" not "0.5000", but 0.0125 keeps
 * its full precision. Uses the browser locale for the decimal separator
 * so Danish users see "0,0125" and en-US users see "0.0125".
 *
 * This helper exists specifically for supplier / purchase / cost values
 * where sub-cent precision is meaningful (e.g. imported EUR unit prices
 * of 0.0125). Sales prices, invoice totals and calculated markups all
 * continue to use ``formatCurrencyAmount`` with 2 decimals — no rounding
 * behaviour there has changed.
 */
export const formatCostPrice = (amount, currency, options = {}) => {
    const { showSymbol = true, showCode = false, maxDecimals = 4, minDecimals = 2 } = options;
    if (amount === null || amount === undefined || amount === '') return '';
    const value = typeof amount === 'number' ? amount : parseFloat(amount);
    if (Number.isNaN(value)) return '';

    const symbols = { EUR: '€', DKK: 'kr', SEK: 'kr', NOK: 'kr', GBP: '£', USD: '$' };
    const formatted = value.toLocaleString(undefined, {
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
    });
    if (showCode) return `${formatted} ${currency}`;
    if (showSymbol) {
        const symbol = symbols[currency] || currency;
        if (['DKK', 'SEK', 'NOK'].includes(currency)) {
            return `${formatted} ${symbol}`;
        }
        return `${symbol}${formatted}`;
    }
    return formatted;
};

/**
 * Custom hook for currency conversion and display.
 * Automatically fetches rates and customer's preferred currency.
 *
 * @param token   Auth token — used to hit `/api/public/customer-currency`
 *                with the visitor's identity. Anonymous callers get 'EUR'.
 * @param options.override  Optional ISO 4217 code. When provided AND the
 *                          visitor is anonymous, this replaces the
 *                          fetched customer preference. Used by the
 *                          public Landing Page + Campaign flows to honour
 *                          `?currency=` handoff from the marketing site.
 */
export const useCurrency = (token = null, options = {}) => {
    const { override } = options;
    const [rates, setRates] = useState(null);
    const [customerCurrency, setCustomerCurrency] = useState(override || 'EUR');
    const [currencySource, setCurrencySource] = useState(override ? 'override' : 'default');
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastFetch, setLastFetch] = useState(null);

    // Fetch exchange rates
    const fetchRates = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/public/exchange-rates`);
            setRates(response.data.rates);
            setLastFetch({
                date: response.data.date,
                updatedAt: response.data.updated_at
            });
            return response.data.rates;
        } catch (err) {
            console.error('Failed to fetch exchange rates:', err);
            setError('Failed to load exchange rates');
            return null;
        }
    }, []);

    // Fetch customer's preferred currency (skipped when an anonymous
    // override is active — the marketing site's `?currency=` handoff
    // wins for the entire session).
    const fetchCustomerCurrency = useCallback(async () => {
        if (override && !token) {
            setCustomerCurrency(override);
            setCurrencySource('override');
            return;
        }
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(`${API}/public/customer-currency`, { headers });
            setCustomerCurrency(response.data.currency);
            setCurrencySource(response.data.source);
        } catch (err) {
            // Default to EUR if not authenticated or error
            setCustomerCurrency(override || 'EUR');
            setCurrencySource(override ? 'override' : 'default');
        }
    }, [token, override]);

    // Fetch currency config (symbols, mappings, etc.)
    const fetchConfig = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/public/currency-config`);
            setConfig(response.data);
        } catch (err) {
            console.error('Failed to fetch currency config:', err);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await Promise.all([
                fetchRates(),
                fetchCustomerCurrency(),
                fetchConfig()
            ]);
            setLoading(false);
        };
        init();
    }, [fetchRates, fetchCustomerCurrency, fetchConfig]);

    // Convert EUR amount to customer's currency
    const toCustomerCurrency = useCallback((amountEur, applyRounding = true) => {
        return convertFromEur(amountEur, customerCurrency, rates, applyRounding);
    }, [customerCurrency, rates]);

    // Format amount in customer's currency
    const formatInCustomerCurrency = useCallback((amountEur, options = {}) => {
        const converted = toCustomerCurrency(amountEur, true);
        return formatCurrencyAmount(converted, customerCurrency, options);
    }, [toCustomerCurrency, customerCurrency]);

    // Format amount showing both EUR and customer currency (for reference)
    const formatWithEurReference = useCallback((amountEur, options = {}) => {
        if (customerCurrency === 'EUR') {
            return formatCurrencyAmount(amountEur, 'EUR', options);
        }
        
        const converted = toCustomerCurrency(amountEur, true);
        const localFormatted = formatCurrencyAmount(converted, customerCurrency, options);
        const eurFormatted = formatCurrencyAmount(amountEur, 'EUR', { ...options, showSymbol: true });
        
        return {
            local: localFormatted,
            eurReference: `≈ ${eurFormatted}`,
            combined: `${localFormatted} (${eurFormatted})`
        };
    }, [customerCurrency, toCustomerCurrency]);

    // Get currency symbol
    const getCurrencySymbol = useCallback((currency = customerCurrency) => {
        return config?.currency_symbols?.[currency] || currency;
    }, [customerCurrency, config]);

    // Get currency name
    const getCurrencyName = useCallback((currency = customerCurrency) => {
        return config?.currency_names?.[currency] || currency;
    }, [customerCurrency, config]);

    // Check if currency uses special rounding (Scandinavian).
    // Falls back to a hardcoded list when the config hasn't been
    // loaded yet — DKK/SEK/NOK all round to nearest 0.50 by regulation.
    const FALLBACK_ROUND_TO_HALF = ['DKK', 'SEK', 'NOK'];
    const usesHalfRounding = useCallback((currency = customerCurrency) => {
        return config?.round_to_half_currencies?.includes(currency) || FALLBACK_ROUND_TO_HALF.includes(currency);
    }, [customerCurrency, config]);

    return {
        // State
        rates,
        customerCurrency,
        currencySource,
        config,
        loading,
        error,
        lastFetch,
        
        // Conversion functions
        toCustomerCurrency,
        convertFromEur: (amount, toCurrency) => convertFromEur(amount, toCurrency, rates),
        convertCurrency: (amount, from, to) => convertCurrency(amount, from, to, rates),
        
        // Formatting functions
        formatInCustomerCurrency,
        formatWithEurReference,
        formatCurrencyAmount: (amount, currency, opts) => formatCurrencyAmount(amount, currency, opts),
        
        // Utility functions
        getCurrencySymbol,
        getCurrencyName,
        usesHalfRounding,
        roundCurrencyAmount,
        
        // Refresh functions
        refreshRates: fetchRates,
        refreshCustomerCurrency: fetchCustomerCurrency
    };
};

export default useCurrency;
